import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { MapApi } from '../api/map-api';
import { UserApi } from '../api/user-api';
import { CreateBasemapsFromConfig } from '../components/mapComponents/basemap-switcher/basemap-config';
import { environment } from '../environment';
import { ESRI } from '../esri-loader-util';
import { LayerService } from '../services/layer.service';
import { IMapcoreFeatureLayer, IMapCoreMap, IMapcoreMapImageLayer, IMCMapView, IUserFolder, IUserMap, MapCoreLayer } from '../types';

import { HeatmapService } from './heatmap.service';

@Injectable({
    providedIn: 'root'
})
export class MapService {

    mcMapView: IMCMapView;
    mcOverviewMapView: IMCMapView;
    map: IMapCoreMap;

    mapStatus: Subject<string> = new Subject<string>();

    constructor(private readonly layerService: LayerService, private readonly heatmapService: HeatmapService, private readonly mapApi: MapApi, private readonly userApi: UserApi) { }

    public async deleteMaps(mapIds: number[]) {
        for (const id of mapIds) {
            await this.mapApi.deleteMap(id);
        }
    }

    public async getSystemMaps(appId: number, includeLayers = true) {
        let props = 'id, name, uniqueName, isSystem, isPublic, description, categories{name}, data, createdDate, lastEditedDate, basemap';
        if (includeLayers) {
            props += ', layers{name}';
        }
        return await this.mapApi.getSystemMaps(appId, props);
    }

    public getMaps = async (mapIds: number[], overrideProps: string = null) => {
        return await this.mapApi.getMaps(mapIds, overrideProps);
    }

    public async getUserMaps(userId: number, applicationId?: number): Promise<{ email: string; firstName: string; lastName: string; userMaps: IUserMap[] }[]> {
        let appIdFilter = '';
        if (applicationId) {
            appIdFilter = `(applicationId:${applicationId})`;
        }
        const props = `firstName, lastName, email, userMaps${appIdFilter} { accessLevel, userId, map(isSystem: false) { id, name, description, data, isSystem, isPublic, createdDate, lastEditedDate, basemap },  sharedWith { accessLevel, userId }}`;
        return this.mapApi.getUserMaps(userId, props);
    }

    public async getUserMap(userId: number, mapId: number, applicationId?: number) {
        return await this.mapApi.getUserMap(userId, mapId, applicationId);
    }

    public async getUserMapsFolders(userId: number, applicationId?: number) {
        const props = `id, folders(category:"maps", applicationId:${applicationId}) {id, applicationId, name,  parentUserFolderId, maps{id} }`;
        return await this.mapApi.getUserMapsFolders(userId, props);
    }

    public async getMapUsers(mapId: number) {
        const props = 'id,  users {id, email, firstName, lastName }';
        return await this.mapApi.getMaps([mapId], props);
    }

    public openMap = async (map: IMapCoreMap, startWithVisibleLayersTurnedOff: boolean = false) => {
        this.mapStatus.next('loading');
        let Extent: __esri.ExtentConstructor;
        [Extent] = await Promise.all([ESRI.Extent]);
        const data = JSON.parse(map.data);
        if (data && data.layers && startWithVisibleLayersTurnedOff) {
            for (const layer of data.layers) {
                layer.visible = false;
            }
        }
        // We should provide prompt to save map before wiping all layers.
        this.mcMapView.resetMapProps(map);
        if (this.isValidExtent(data.extent)) {
            this.mcMapView.extent = Extent.fromJSON(data.extent);
        }

        // change if there's a saved basemap from db
        if (map.basemap) {
            const basemapSetup = JSON.parse(map.basemap);

            switch (basemapSetup.type) {
                case 'ID':
                    this.layerService.mapRef.basemap = await this.createBasemapFromId(basemapSetup.id);
                    break;
                case 'BING':
                    this.layerService.mapRef.basemap = await this.createBingBasemap(basemapSetup.id);
                    break;
                case 'CONFIG':
                    const basemapConfig = await this.createBasemapConfig(basemapSetup.id);
                    await this.layerService.getLayerCopyRight(basemapConfig);
                    this.layerService.mapRef.basemap = await CreateBasemapsFromConfig(basemapConfig);
                    break;
                default:
                    const pennDOTBasemapConfig = await this.createBasemapConfig('PennDOT');
                    await this.layerService.getLayerCopyRight(pennDOTBasemapConfig);
                    this.layerService.mapRef.basemap = await CreateBasemapsFromConfig(pennDOTBasemapConfig);
                    break;
            }
        }

        let layerReferencesInBulk = [];
        for (const layer of data.layers) {
            if (layer.id) {
                layerReferencesInBulk.push(layer);
            } else if (layer.layers) {
                if (layerReferencesInBulk.length > 0) {
                    await this.layerService.addMapcoreLayersToMap(layerReferencesInBulk, null, false, map.isSystem);
                }
                layerReferencesInBulk = [];
                for (const childLayer of layer.layers) {
                    childLayer.groupParent = {
                        name: layer.name,
                        visible: layer.visible,
                        isOpen: layer.isExpanded,
                        splitThemeJSON: layer.splitThemeJSON
                    };
                }
                await this.layerService.addMapcoreLayersToMap(layer.layers, null, false, map.isSystem);
            }
        }
        if (layerReferencesInBulk.length > 0) {
            await this.layerService.addMapcoreLayersToMap(layerReferencesInBulk, null, false, map.isSystem, data.paramConfig);
        }
        this.mapStatus.next('loaded');
    }

    private isValidExtent(extent?: { xmin: number; xmax: number; ymin: number; ymax: number }) {
        return extent && extent.xmin && extent.xmax && extent.ymin && extent.ymax;
    }

    public resetMap() {
        this.mcMapView.resetMapProps();
    }

    public async retrieveMap(map: IMapCoreMap, userId: number) {
        const mapId = map.id;
        if (map.isSystem) {
            const props = 'id, name, uniqueName, isSystem, isPublic, description';
            const maps = await this.mapApi.getMaps([mapId], props);
            if (maps && maps.length > 0) {
                const sysMap = maps[0];
                map.accessLevel = 'VIEWER';
                return sysMap;
            } else {
                return map;
            }
        } else {
            const props = `userMaps { accessLevel, map (id:${mapId}) {id, name, uniqueName, isSystem, isPublic, description} }`;
            const result = await this.mapApi.getUserMaps(userId, props);
            //TODO: sanitizeMap
            if (result && result.length > 0 && result[0].userMaps.length > 0) {
                const userMap = result[0].userMaps.find(um => {
                    return um.map && um.map.id === mapId;
                });
                if (userMap) {
                    const mapObj = userMap.map;
                    mapObj.accessLevel = userMap.accessLevel;
                    return mapObj;
                } else {
                    return map;
                }

            } else {
                return map;
            }
        }
    }

    public async shareMap(mapId, data, notifyUserByEmail, sharedUserIds) {
        let emailSubject = '';
        let emailBody = '';
        let resharedEmailSubject = '';
        let resharedEmailBody = '';
        const emailmessage: any = data.data.map((u: any) => u.message);
        const message = emailmessage.find(() => true);
        if (notifyUserByEmail) {
            resharedEmailSubject = environment.appName + ': Resharing Map';
            emailSubject = environment.appName + ': New Shared Map';
            resharedEmailBody = this._buildReSharedMessage(message, data.title ? data.title : data.data[0].title, mapId);
            emailBody = this._buildMessage(message, data.title ? data.title : data.data[0].title, mapId);
        }
        const userIds: number[] = [];
        const userIdShared: number[] = [];
        data.recipients.forEach((recipient) => {
            for (const sharedUserId of sharedUserIds) {
                const userShared = sharedUserId.includes(recipient.id);
                if (userShared) {
                    userIdShared.push(recipient.id);
                } else {
                    userIds.push(recipient.id);
                }
            }
        });
        let result: any;
        if (userIds.length > 0) {
            result = await this.mapApi.shareMap(userIds, mapId, emailSubject, emailBody);
        }
        if (userIdShared.length > 0) {
            result = await this.mapApi.shareMap(userIdShared, mapId, resharedEmailSubject, resharedEmailBody);
        }
        return result;
    }

    public async shareMaps(mapIds, data, notifyUserByEmail, sharedUserIds) {
        let emailSubject = '';
        let emailBody = '';
        let resharedEmailSubject = '';
        let resharedEmailBody = '';
        const emailmessage: any = data.data.map((u: any) => u.message);
        const message = emailmessage.find(() => true);
        const mapTitles = data.data.map(map => map.title);
        if (notifyUserByEmail) {
            resharedEmailSubject = environment.appName + (mapIds.length > 1 ? ': Resharing Maps' : ': Resharing Map');
            emailSubject = environment.appName + (mapIds.length > 1 ? ': New Shared Maps' : ': New Shared Map');
            resharedEmailBody = this._buildReSharedMessageMulti(message, mapTitles, mapIds);
            emailBody = this._buildMessageMulti(message, mapTitles, mapIds);
        }
        const userIds: number[] = [];
        const userIdShared: number[] = [];
        data.recipients.forEach((recipient) => {
            for (const sharedUserId of sharedUserIds) {
                const userShared = sharedUserId.includes(recipient.id);
                if (userShared) {
                    if (!userIdShared.includes(recipient.id)) {
                        userIdShared.push(recipient.id);
                    }
                } else {
                    if (!userIds.includes(recipient.id)) {
                        userIds.push(recipient.id);
                    }
                }
            }
        });
        let result: any;
        if (userIds.length > 0) {
            result = await this.mapApi.shareMaps(userIds, mapIds, emailSubject, emailBody);
        }
        if (userIdShared.length > 0) {
            result = await this.mapApi.shareMaps(userIdShared, mapIds, resharedEmailSubject, resharedEmailBody);
        }
        return result;
    }

    public async unShareMap(userIds, mapId) {

        await this.mapApi.unShareMap(userIds, mapId);
    }

    public getMapUrl(mapId) {
        const baseUrl = environment.deepLinkBaseUrl || window.location.href.split('?')[0];
        return `${baseUrl}?map-id=${mapId}`;
    }

    public async copyMaps(mapIds: number[], appId: number) {
        return await this.mapApi.copyMaps(mapIds, appId);
    }
    public async setTokenPcit(token: string): Promise<string | Event > {
        return this.mapApi.setTokenCookie(token, `${environment.apiBaseUrl}/cookies/pcit`);
    }

    async turnOnHeatmapMode(layer: IMapcoreMapImageLayer | IMapcoreFeatureLayer, heatmapRenderer = null) {
        this.heatmapService.updateHeatmapLayers(layer, heatmapRenderer);
    }

    private _buildMessage(message, title, mapId): string {
        const appName = environment.appName ? environment.appName : '';
        const mapUrl = this.getMapUrl(mapId);
        let template = `I\'ve shared a new map with you from ${appName} named ${title}. You can view the map in ${appName} by going to the Maps dialog.\n\n ${mapUrl}`;
        if (message) {
            template += `\n\nMessage from user: ${message}`;
        }
        return template;
    }

    private _buildMessageMulti(message, titles, mapIds): string {
        const appName = environment.appName ? environment.appName : '';
        const mapUrls = mapIds.map(id => this.getMapUrl(id));
        const mapsText = mapUrls.map((url, index) => `${titles[index]}: ${url}`);
        let template = `I\'ve shared maps with you from ${appName}. You can view the maps in ${appName} by going to the Maps dialog.\n\n${mapsText.join('\n')}`;

        if (message) {
            template += `\n\nMessage from user: ${message}`;
        }
        return template;
    }

    private _buildReSharedMessage(message, title, mapId): string {
        const appName = environment.appName ? environment.appName : '';
        const mapUrl = this.getMapUrl(mapId);
        let template = `I am resharing a map with you from ${appName} named ${title}. You can view the map in ${appName} by going to the Maps dialog.\n\n ${mapUrl}`;
        if (message) {
            template += `\n\nMessage from user: ${message}`;
        }
        return template;
    }

    private _buildReSharedMessageMulti(message, titles, mapIds): string {
        const appName = environment.appName ? environment.appName : '';
        const mapUrls = mapIds.map(id => this.getMapUrl(id));
        const mapsText = mapUrls.map((url, index) => `${titles[index]}: ${url}`);
        let template = `I am resharing maps with you from ${appName}. You can view the maps in ${appName} by going to the Maps dialog.\n\n${mapsText.join('\n')}`;
        if (message) {
            template += `\n\nMessage from user: ${message}`;
        }
        return template;
    }

    public clearLoading() {
        this.mapStatus.next('loaded');
    }

    public async saveMap(map: IMapCoreMap, isNewMap: boolean = false, parentFolderId: number = 0, newFolder: IUserFolder = null) {
        let layers = (<any>this.layerService.mapRef.layers).items as MapCoreLayer[];
        layers = layers.filter(x =>
            x.mapcoreProps &&
            (x.mapcoreProps.parentId || x.mapcoreProps.url || 'graphics' in x || 'source' in x || x.type === 'group') &&
            !x.mapcoreProps.hideInLegend && (!x.listMode || x.listMode !== 'hide'));

        const mapLayers = [];
        for (const layer of layers) {
            if (layer.type !== 'group') {
                const mapcoreLayer = this.layerService.getLayerForSaveMap(layer, isNewMap);
                mapLayers.push(mapcoreLayer);
            } else {
                for (const groupedLayer of layer.layers.toArray()) {
                    const groupedMapcoreLayer = this.layerService.getLayerForSaveMap(groupedLayer, isNewMap);
                    mapLayers.push(groupedMapcoreLayer);
                }
            }
        }

        if (this.layerService.mapRef.basemap) {
            // 2/16/24, Onemap uses terrain basemap with id "Terrain" (which will hit else below) and MIQ switched to Esri terrain basemap with id "terrain"
            const basemapIds = ['terrain', 'streets-vector', 'satellite', 'hybrid', 'topo-vector', 'gray-vector', 'osm'];
            if (basemapIds.includes(this.layerService.mapRef.basemap.id)) {
                map.basemap = JSON.stringify({ type: 'ID', id: `${this.layerService.mapRef.basemap.id}` });
            } else if (this.layerService.mapRef.basemap.id.includes('Bing')) {
                map.basemap = JSON.stringify({ type: 'BING', id: `${this.layerService.mapRef.basemap.id}` });
            } else {
                map.basemap = JSON.stringify({ type: 'CONFIG', id: `${this.layerService.mapRef.basemap.id}` });
            }
        }

        if (map.isSystem || isNewMap) {
            map.id = null;
            map.isSystem = false;
            map.layers = null;
        }
        map.extentJSON = JSON.stringify(this.mcMapView.extent.toJSON());

        if (!map) {
            map = (<any>this.mcMapView).mapcoreViewProps;
        }
        map = await this.mapApi.saveMap(map, mapLayers, environment.appId);
        (<any>this.mcMapView).mapcoreViewProps = map;

        if (newFolder) {
            const currentFolder = await this.userApi.createNewUserFolder(newFolder);
            if (currentFolder) {
                map.parentFolderId = currentFolder.id;
            }
        }

        if (!map.parentFolderId) {
            map.parentFolderId = parentFolderId;
        }

        if (map.parentFolderId) {
            await this.mapApi.saveMapToUserFolder(map.id, map.parentFolderId);
        }

        return map.id;
    }

    public async createBasemapFromId(basemapId) {
        let Basemap: __esri.BasemapConstructor;

        if (!Basemap) {
            [Basemap] = await Promise.all([ESRI.Basemap]);
        }
        return Basemap.fromId(basemapId);
    }

    public async createBingBasemap(basemapId) {
        let bingBasemap;
        let bingSetupConfig;

        // default to same as BingMapsRoad
        switch (basemapId.slice(0, -1)) {
            case 'BingMapsRoad':
                bingSetupConfig = {
                    style: 'road',
                    title: 'Bing Streets',
                    thumbnailUrl: environment.bingBasemaps.thumbnails.street,
                    id: basemapId
                };
                bingBasemap = this.createBingBasemapFromConfig(bingSetupConfig);
                break;
            case 'BingMapsAerial':
                bingSetupConfig = {
                    style: 'aerial',
                    title: 'Bing Imagery',
                    thumbnailUrl: environment.bingBasemaps.thumbnails.imagery,
                    id: basemapId
                };
                bingBasemap = this.createBingBasemapFromConfig(bingSetupConfig);
                break;
            case 'BingMapsHybrid':
                bingSetupConfig = {
                    style: 'hybrid',
                    title: 'Bing Imagery w/ Labels',
                    thumbnailUrl: environment.bingBasemaps.thumbnails.hybrid,
                    id: basemapId
                };
                bingBasemap = this.createBingBasemapFromConfig(bingSetupConfig);
                break;
            default:
                bingSetupConfig = {
                    style: 'road',
                    title: 'Bing Streets',
                    thumbnailUrl: environment.bingBasemaps.thumbnails.street,
                    id: basemapId
                };
                bingBasemap = this.createBingBasemapFromConfig(bingSetupConfig);
                break;
        }
        return bingBasemap;
    }

    public async createBingBasemapFromConfig(bingSetupConfig) {
        let Basemap: __esri.BasemapConstructor;
        let BingMapLayer: __esri.BingMapsLayerConstructor;
        let bingBasemap;
        [Basemap, BingMapLayer] = await Promise.all([ESRI.Basemap, ESRI.BingMapsLayer]);
        const opacityModifier = 0.000001;

        bingBasemap = new Basemap({
            baseLayers: [
                new BingMapLayer({
                    ...bingSetupConfig,
                    key: environment.bingBasemaps.key,
                    opacity: 1 - opacityModifier //this setting of opacity is a work around for ESRI comparisons that just take into consideration type, url, min scale, max scale, visibility, and opacity. In the
                    //case of BingMaps, URL and Type are always equivalent ('bing-maps' and 'null') and the others are not set by default. Overriding the opacity to make sure that each map is considered
                    //unique by this comparison. Found in dojo.js formatted line dojo.js formatted line 22302
                })
            ],
            ...bingSetupConfig
        });

        return bingBasemap;
    }

    public async createBasemapConfig(basemapId) {
        let basemapConfig;
        switch (basemapId) {
            case 'Elevation':
                basemapConfig = {
                    tileUrl: 'https://services.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile',
                    thumbnailTile: '/15/12358/9365',
                    tileTemplate: '/{level}/{row}/{col}',
                    title: 'Elevation'
                };
                break;
            case 'Topographic DRG':
                basemapConfig = {
                    tileUrl: 'https://services.arcgisonline.com/arcgis/rest/services/USA_Topo_Maps/MapServer/tile',
                    thumbnailTile: '/7/48/36',
                    tileTemplate: '/{level}/{row}/{col}',
                    title: 'Topographic DRG'
                };
                break;
            // MIQ fazed this out with Esri's terrain basemap.. Onemap and others still use
            case 'Terrain':
                basemapConfig = {
                    tileUrl: 'https://tiles.stadiamaps.com/tiles/stamen_terrain',
                    thumbnailTile: '/10/177/409.png',
                    tileTemplate: '/{level}/{col}/{row}.png',
                    title: 'Terrain'
                };
                break;
            case 'PEMA':
                basemapConfig = {
                    tileUrl: 'https://gis.penndot.pa.gov/img/rest/services/imagery/PEMA_2018/ImageServer/tile',
                    thumbnailTile: '/13/3093/2346',
                    tileTemplate: '/{level}/{row}/{col}',
                    title: 'PEMA'
                };
                break;
            default:
                basemapConfig = environment.pennDotBaseMap;
                break;
        }

        return basemapConfig;
    }
}
